import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  OnInit,
  WritableSignal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DsBoxModule, DsButtonModule, DsFormFieldModule, DsRadioGroupModule } from '@bmw-ds/components';

import { AddressField } from '../../../customer/model/address-field.model';
import { CustomerAddress } from '../../../customer/model/customer-information.model';
import { ExtendedCustomer } from '../../../customer/model/extended-customer.model';
import { AddressViewService } from '../../../customer/services/address.view-service';
import { FeatureVisibilityService } from '../../../customer/services/feature-visibility.service';
import {
  getMissingBusinessAddressFields,
  getMissingPrivateAddressFields,
} from '../../../customer/services/mandatory-address';
import { I18nService, useI18n } from '../../../i18n/services/i18n.service';
import { lensProxy } from '../../../utils/lensProxy/lensProxy';
import { CustomerComparisonField } from '../../model/customer-comparison-field.model';
import { CustomerComparisonRowComponent } from './customer-comparison-row.component';

@Component({
  selector: 'cdf-customer-comparison-row-container',
  imports: [
    DsBoxModule,
    DsRadioGroupModule,
    DsFormFieldModule,
    FormsModule,
    DsButtonModule,
    CustomerComparisonRowComponent,
  ],
  template: `
    <cdf-customer-comparison-row
      [(comparisonFieldValue)]="combinedCustomerComparisonFieldValue"
      [retailLabel]="retailCustomerLabel()"
      [retailValue]="retailCustomerValue()"
      [nscLabel]="nscCustomerLabel()"
      [nscValue]="nscCustomerValue()"
      [comparisonFieldLabel]="i18n.enum(comparisonField(), 'customerComparisonField')"
      [comparisonFieldType]="comparisonField()"
      [mandatoryField]="isMandatoryField()"
      [retailDisabled]="retailOptionDisabled()"
      [nscDisabled]="nscOptionDisabled()"
    />
  `,
  styleUrl: './customer-comparison-row.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerComparisonRowContainer implements OnInit {
  private readonly featureVisibilityService = inject(FeatureVisibilityService);
  protected readonly i18n = useI18n();
  protected readonly i18nService = inject(I18nService);

  private readonly addressViewService = inject(AddressViewService);

  readonly comparisonField = input.required<CustomerComparisonField>();
  readonly retailCustomer = input.required<ExtendedCustomer>();
  readonly nscCustomer = input.required<ExtendedCustomer>();

  readonly combinedCustomer = model.required<ExtendedCustomer>();
  readonly combinedCustomerProxy = lensProxy(this.combinedCustomer);

  /**
   * Fields without a "neither" option. The private address is mandatory (country, city and postal
   * code, see mandatory-address.ts); the business address is optional and keeps "neither".
   */
  private readonly mandatoryFields: readonly CustomerComparisonField[] = [
    CustomerComparisonField.FIRST_NAME,
    CustomerComparisonField.LAST_NAME,
    CustomerComparisonField.EMAIL_PRIVATE,
    CustomerComparisonField.PRIVATE_ADDRESS,
  ];

  /**
   * This map contains all field names of the interface CustomerComparison as values which define equality between two CustomerComparison objects.
   * Some keys are excluded from the equality-comparison, like GCID and accountNumber.
   */
  static readonly extendedCustomerFieldsMap: Record<CustomerComparisonField, keyof ExtendedCustomer> = {
    [CustomerComparisonField.FIRST_NAME]: 'firstName',
    [CustomerComparisonField.LAST_NAME]: 'lastName',
    [CustomerComparisonField.NAME_INITIALS]: 'nameInitials',
    [CustomerComparisonField.SUFFIX]: 'suffix',
    [CustomerComparisonField.BIRTHDATE]: 'birthdate',
    [CustomerComparisonField.MIDDLE_NAME]: 'middleName',
    [CustomerComparisonField.MOBILE_BUSINESS]: 'mobileBusiness',
    [CustomerComparisonField.MOBILE_PRIVATE]: 'mobilePrivate',
    [CustomerComparisonField.PHONE_BUSINESS]: 'phoneBusiness',
    [CustomerComparisonField.PHONE_PRIVATE]: 'phonePrivate',
    [CustomerComparisonField.SALUTATION]: 'salutation',
    [CustomerComparisonField.TITLE]: 'title',
    [CustomerComparisonField.PRIVATE_ADDRESS]: 'privateAddress',
    [CustomerComparisonField.BUSINESS_ADDRESS]: 'businessAddress',
    [CustomerComparisonField.MAIN_ADDRESS]: 'mainAddress',
    [CustomerComparisonField.VAT_GROUP]: 'vatGroup',
    [CustomerComparisonField.IS_INSOLVENT]: 'isInsolvent',
    [CustomerComparisonField.PLACE_OF_BIRTH]: 'placeOfBirth',
    [CustomerComparisonField.EMAIL_BUSINESS]: 'emailBusiness',
    [CustomerComparisonField.EMAIL_PRIVATE]: 'emailPrivate',
    [CustomerComparisonField.CORRESPONDENCE_LANGUAGE]: 'correspondenceLanguage',
    [CustomerComparisonField.NATIONAL_ID_1]: 'nationalId1',
    [CustomerComparisonField.NATIONAL_ID_1_DOCUMENT_TYPE]: 'nationalId1DocumentType',
    [CustomerComparisonField.NATIONALITY]: 'nationality',
    [CustomerComparisonField.SELF_EMPLOYED]: 'selfEmployed',
    [CustomerComparisonField.CUSTOMER_MAIN_TYPE]: 'customerMainType',
    [CustomerComparisonField.DESCRIPTION]: 'description',
    [CustomerComparisonField.TAX_NUMBER]: 'taxNumber',
    [CustomerComparisonField.TAX_NUMBER_COUNTRY]: 'taxNumberCountry',
  };

  protected readonly isMandatoryField = computed(() => this.mandatoryFields.includes(this.comparisonField()));

  private readonly isPrivateAddressRow = computed(
    () => this.comparisonField() === CustomerComparisonField.PRIVATE_ADDRESS,
  );

  private readonly isBusinessAddressRow = computed(
    () => this.comparisonField() === CustomerComparisonField.BUSINESS_ADDRESS,
  );

  protected readonly nscCustomerLabel = computed(() => this.getFieldLabel(this.nscCustomer()));
  protected readonly nscCustomerValue = computed(() => this.getFieldValue(this.nscCustomer()));
  protected readonly retailCustomerLabel = computed(() => this.getFieldLabel(this.retailCustomer()));
  protected readonly retailCustomerValue = computed(() => this.getFieldValue(this.retailCustomer()));

  /** An address option that the NSC Database write would reject must not be selectable. */
  protected readonly retailOptionDisabled = computed(() => this.isUnusableAddress(this.retailCustomerValue()));
  protected readonly nscOptionDisabled = computed(() => this.isUnusableAddress(this.nscCustomerValue()));

  ngOnInit(): void {
    // Defaulting to NSC Database values
    this.setFieldValueFromCustomer(this.nscCustomer());

    const field = CustomerComparisonRowContainer.extendedCustomerFieldsMap[this.comparisonField()];
    const nscDefault = this.combinedCustomer()[field];
    const retailValue = this.retailCustomer()[field];

    if (this.isPrivateAddressRow() || this.isBusinessAddressRow()) {
      // An unusable NSC default (incomplete address) falls back to Retail if Retail is usable.
      // If neither is usable, the default stays: the sync is then blocked until the user completes
      // the data - or, for the optional business address, explicitly chooses "neither". It is not
      // set to "neither" automatically, as that would delete the stored NSC business address.
      if (this.isUnusableAddress(nscDefault) && !this.isUnusableAddress(retailValue)) {
        this.combinedCustomer.update((customer) => ({ ...customer, [field]: retailValue }));
      }
      return;
    }

    // If default NSC Database values for mandatory fields are undefined, instead take values from Retail
    if (this.isMandatoryField() && !nscDefault) {
      this.combinedCustomer.update((customer) => ({ ...customer, [field]: retailValue }));
    }
  }

  private isUnusableAddress(value: unknown): boolean {
    const address = value as CustomerAddress | undefined;
    if (this.isPrivateAddressRow()) {
      return getMissingPrivateAddressFields(address).length > 0;
    }
    if (this.isBusinessAddressRow()) {
      return getMissingBusinessAddressFields(address).length > 0;
    }
    return false;
  }

  private setFieldValueFromCustomer(customer: ExtendedCustomer | undefined) {
    const field = CustomerComparisonRowContainer.extendedCustomerFieldsMap[this.comparisonField()];
    if (field) {
      this.combinedCustomer.update((combinedCustomer) => ({
        ...combinedCustomer,
        [field]: customer?.[field],
      }));
    } else {
      throw new Error(
        `#CDFTF0130 Unexpected comparison field which is not covered by logic occurred: ${this.comparisonField()}`,
      );
    }
  }

  private getFieldLabel(customer: ExtendedCustomer): string | undefined {
    switch (this.comparisonField()) {
      case CustomerComparisonField.NATIONAL_ID_1_DOCUMENT_TYPE:
        return this.i18n.enum(customer.nationalId1DocumentType, 'nationalIdDocumentType', '');
      case CustomerComparisonField.SELF_EMPLOYED:
        if (customer.selfEmployed === undefined) {
          return undefined;
        }
        return customer.selfEmployed
          ? this.i18n('selfEmployed.SELF_EMPLOYED')
          : this.i18n('selfEmployed.NOT_SELF_EMPLOYED');
      case CustomerComparisonField.CUSTOMER_MAIN_TYPE:
        return this.i18n.enum(customer.customerMainType, 'customerMainType', '');
      case CustomerComparisonField.MAIN_ADDRESS:
        return this.i18n.enum(customer.mainAddress, 'mainAddress', '');
      case CustomerComparisonField.TITLE:
        return this.i18n.enum(customer.title, 'titles', '');
      case CustomerComparisonField.SALUTATION:
        return this.i18n.enum(customer.salutation, 'salutations', '');
      case CustomerComparisonField.IS_INSOLVENT:
        if (customer.isInsolvent === undefined) {
          return undefined;
        }
        return customer.isInsolvent ? this.i18n('insolvency.isInsolvent') : this.i18n('insolvency.isNotInsolvent');
      case CustomerComparisonField.VAT_GROUP:
        return this.i18n.enum(customer.vatGroup, 'vatGroup', '');
      case CustomerComparisonField.BIRTHDATE:
        return customer.birthdate
          ? new Date(customer.birthdate).toLocaleDateString(this.i18nService.activeLanguage())
          : undefined;
      case CustomerComparisonField.PRIVATE_ADDRESS:
        return customer.privateAddress
          ? this.addressViewService.getAsSingleLineStringWithOptionalFields(
              customer.privateAddress,
              customer.privateAddress.country,
              !this.featureVisibilityService.isHidden(AddressField.PROVINCE),
            )
          : undefined;
      case CustomerComparisonField.BUSINESS_ADDRESS:
        return customer.businessAddress
          ? this.addressViewService.getAsSingleLineStringWithOptionalFields(
              customer.businessAddress,
              customer.businessAddress.country,
              !this.featureVisibilityService.isHidden(AddressField.PROVINCE),
            )
          : undefined;
      default:
        // Each of the remaining fields are string values
        return this.getFieldValue(customer) as string | undefined;
    }
  }

  private getFieldValue(customer: ExtendedCustomer): string | boolean | CustomerAddress | undefined {
    const field = CustomerComparisonRowContainer.extendedCustomerFieldsMap[this.comparisonField()];
    return customer[field];
  }

  protected get combinedCustomerComparisonFieldValue(): WritableSignal<string | boolean | CustomerAddress | undefined> {
    const field = CustomerComparisonRowContainer.extendedCustomerFieldsMap[this.comparisonField()];
    return this.combinedCustomerProxy[field];
  }
}
